import { Component, OnInit, Output, EventEmitter, OnDestroy, NgZone } from '@angular/core';
import { StorageHelper } from "../helpers/StorageHelper";
import { Router } from "@angular/router";
import { AuthenticationService } from "../authentication.service";
import { Global } from "../classes/Global";
import { BaseComponent } from "../classes/BaseComponent";
import { ValidationHelper } from "../helpers/ValidationHelper";
import { InternationalizationHelper } from '../helpers/InternationalizationHelper';
import { IFacebookEventListener } from '../classes/IFacebookEventListener';
import { Facebook } from '../classes/Facebook';
import { LoginHelper } from '../helpers/LoginHelper';
import { Log } from '../helpers/Log';
import { SettingsService } from '../settings.service';

declare var jsSHA: any;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [ AuthenticationService ]
})
export class LoginComponent extends BaseComponent implements OnInit, OnDestroy, IFacebookEventListener {

  shaObj: any;
  success = true;
  serverError = false;
  serverErrorMessage: string;
  email = "";
  password = "";
  language: string = InternationalizationHelper.getLanguage();
  facebook: Facebook = Facebook.getInstance();
  emailInvalid = false;
  formIncomplete = false;

  @Output() loginSuccess = new EventEmitter();
  @Output() onResetPassword = new EventEmitter();
  @Output() onRegisterClick = new EventEmitter();

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private zone: NgZone,
    private settingsService: SettingsService) {
    super();
  }

  ngOnInit() {

    this.facebook.addEventListener(this);

    if (this.authenticationService.isLoggedIn()){
      this.loginSuccess.emit();
    }

  }

  ngOnDestroy(): void {
    this.facebook.removeEventListener(this);
  }

  onFacebookConnected(response) {
    this.facebook.removeEventListener(this);
    const self = this;

    this.facebook.getFBRef().api('/me', { locale: self.language, fields: 'id,name,picture,email' },
    function(graphAPIResponse) {

      if (graphAPIResponse.email) {
        self.authenticationService.loginByFb(Global.getCountry(), graphAPIResponse.email,
        response.authResponse.accessToken)
        .subscribe(res => {
          //run with zone because the facebook API does not work with observables and
          //otherwise Angular does not refresh the UI until a click is done
          self.zone.run(() => {
            self.handleResult(res);
          });
        }, err => {
          self.zone.run(() => {
            self.handleError(err);
          });
        });
      }
      else {
        //The email of the user is missing
        self.success = false;
        self.serverError = true;
        self.serverErrorMessage = self.getString("settingsError");
      }

    });
  }

  FBLogin() {
    this.facebook.login();
  }

  login() {

    //Check if all fields are filled
    if (!ValidationHelper.isValidString(this.email) ||
      !ValidationHelper.isValidString(this.password)) {
        this.formIncomplete = true;
        return;
    }

    //Check if the email address is valid
    if (!ValidationHelper.isValidEmail(this.email)) {
      this.emailInvalid = true;
      return;
    }

    //Re-initialize error flags
    this.formIncomplete = false;
    this.emailInvalid = false;
    this.success = true;
    this.serverError = false;

    this.authenticationService.login(Global.getCountry(), this.email, this.hashPassword()).subscribe(
      result => {
        this.handleResult(result);
      },
      error => {
        this.handleError(error);
      });
  }

  hashPassword(): string {
    this.shaObj = new jsSHA("SHA-256", "TEXT");
    this.shaObj.update(this.password);
    return this.shaObj.getHash("HEX");
  }

  handleResult(result) {
    if (result && result.status === "success" && result.additionalInfo) {
      StorageHelper.getInstance().save(StorageHelper.keys.email, this.email);
      StorageHelper.getInstance().save(StorageHelper.keys.sessionToken, result.additionalInfo.sessionToken);
      StorageHelper.getInstance().save(StorageHelper.keys.username, result.additionalInfo.username);
      StorageHelper.getInstance().save(StorageHelper.keys.profileImageUrl, result.additionalInfo.profileImageUrl);
      StorageHelper.getInstance().save(StorageHelper.keys.uniqueCustomerId, result.additionalInfo.uniqueCustomerId);
      this.authenticationService.setIsLoggedIn(true);
      LoginHelper.getInstance().loginChanged(true);
      this.loginSuccess.emit();

    }
    else {
      this.success = false;
      this.serverError = true;
      this.serverErrorMessage = result && result.message ? result.message : this.getString("emailOrPasswordInvalid");
    }
  }

  handleError(error) {
    Log.getInstance().d(error);
  }

  onPasswordChange(password) {
    this.password = password;
  }

  onPasswordEnterPressed() {
    this.login();
  }

  onEmailChange(email) {
    this.email = email;
  }

  resetPasswordClicked() {
    this.onResetPassword.emit();
  }

  registerClicked() {
    this.onRegisterClick.emit();
  }

}
